import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { voiceAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
    Mic,
    Upload,
    FileAudio,
    Play,
    Pause,
    Sparkles,
    AlertCircle,
    CheckCircle,
    XCircle
} from 'lucide-react';

export default function VoiceAnalysisPage() {
    const [file, setFile] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            // Check file type
            const allowedTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/m4a', 'audio/webm'];
            if (!allowedTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(mp3|wav|m4a|mp4|webm|mpeg)$/i)) {
                setError('Please upload a valid audio file (MP3, WAV, M4A, MP4, or WebM)');
                return;
            }
            // Check file size (25MB max)
            if (selectedFile.size > 25 * 1024 * 1024) {
                setError('File size must be less than 25MB');
                return;
            }
            setFile(selectedFile);
            setError('');
            setResult(null);
        }
    };

    const handleAnalyze = async () => {
        if (!file) return;
        
        try {
            setAnalyzing(true);
            setError('');
            const response = await voiceAPI.analyze(file);
            setResult(response.data);
        } catch (err) {
            console.error('Analysis failed:', err);
            setError(err.response?.data?.detail || 'Failed to analyze audio. Please try again.');
        } finally {
            setAnalyzing(false);
        }
    };

    const getSentimentIcon = (sentiment) => {
        switch (sentiment) {
            case 'positive':
                return <CheckCircle className="w-5 h-5 text-emerald-500" />;
            case 'negative':
                return <XCircle className="w-5 h-5 text-red-500" />;
            default:
                return <AlertCircle className="w-5 h-5 text-amber-500" />;
        }
    };

    const getBurnoutColor = (risk) => {
        switch (risk) {
            case 'high':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'medium':
                return 'bg-amber-100 text-amber-800 border-amber-200';
            default:
                return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        }
    };

    return (
        <div className="p-8" data-testid="voice-analysis-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold font-heading text-stone-900 flex items-center gap-3">
                        <Mic className="w-8 h-8 text-indigo-600" />
                        Voice Analysis
                    </h1>
                    <p className="text-stone-500 mt-1">
                        Upload audio from meetings or interviews for sentiment and behavioral analysis.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Upload Card */}
                    <Card className="bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Upload Audio</CardTitle>
                            <CardDescription>
                                Supported formats: MP3, WAV, M4A, MP4, WebM (max 25MB)
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-stone-200 rounded-2xl p-8 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-all"
                                data-testid="upload-dropzone"
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    accept="audio/*,.mp3,.wav,.m4a,.mp4,.webm"
                                    className="hidden"
                                    data-testid="file-input"
                                />
                                <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                                    <Upload className="w-8 h-8 text-indigo-600" />
                                </div>
                                <p className="text-stone-900 font-medium mb-1">
                                    Click to upload or drag and drop
                                </p>
                                <p className="text-sm text-stone-500">
                                    Audio files up to 25MB
                                </p>
                            </div>

                            {file && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                                        <FileAudio className="w-5 h-5 text-indigo-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-stone-900 truncate">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-stone-500">
                                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setFile(null);
                                            setResult(null);
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </motion.div>
                            )}

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
                                >
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    {error}
                                </motion.div>
                            )}

                            <Button
                                onClick={handleAnalyze}
                                disabled={!file || analyzing}
                                className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700"
                                data-testid="analyze-voice-btn"
                            >
                                {analyzing ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Analyzing... (this may take a moment)
                                    </span>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        Analyze Audio
                                    </>
                                )}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Results Card */}
                    <Card className="bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Analysis Results</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {result ? (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="space-y-6"
                                >
                                    {/* Conversation Type & Speakers */}
                                    {result.num_speakers > 1 && (
                                        <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">
                                                    {result.num_speakers} Speakers Detected
                                                </Badge>
                                                <Badge variant="outline" className="capitalize">
                                                    {result.conversation_type || 'Conversation'}
                                                </Badge>
                                            </div>
                                            {result.speakers?.length > 0 && (
                                                <div className="space-y-2">
                                                    {result.speakers.map((speaker, i) => (
                                                        <div key={i} className="flex items-center justify-between text-sm">
                                                            <span className="font-medium text-indigo-900">{speaker.label}</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-indigo-600 capitalize">{speaker.estimated_role}</span>
                                                                <Badge className={`text-xs ${
                                                                    speaker.overall_sentiment === 'positive' ? 'bg-emerald-100 text-emerald-800' :
                                                                    speaker.overall_sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                                                    'bg-amber-100 text-amber-800'
                                                                }`}>
                                                                    {speaker.overall_sentiment}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {result.conversation_summary && (
                                                <p className="text-sm text-indigo-700 mt-3 pt-3 border-t border-indigo-200">
                                                    {result.conversation_summary}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Sentiment & Score */}
                                    <div className="flex items-center gap-4">
                                        <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
                                            result.sentiment === 'positive' ? 'bg-emerald-50 border-emerald-200' :
                                            result.sentiment === 'negative' ? 'bg-red-50 border-red-200' :
                                            'bg-amber-50 border-amber-200'
                                        }`}>
                                            {getSentimentIcon(result.sentiment)}
                                            <span className="font-medium capitalize">{result.sentiment}</span>
                                        </div>
                                        <div className="text-sm text-stone-600">
                                            Score: <span className="font-mono font-medium">{result.score?.toFixed(2)}</span>
                                        </div>
                                    </div>

                                    {/* Burnout Risk */}
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-2">Burnout Risk</p>
                                        <Badge className={`${getBurnoutColor(result.burnout_risk)} capitalize`}>
                                            {result.burnout_risk || 'Low'}
                                        </Badge>
                                    </div>

                                    {/* Emotions */}
                                    {result.emotions && Object.keys(result.emotions).length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium text-stone-700 mb-3">Detected Emotions</p>
                                            <div className="space-y-2">
                                                {Object.entries(result.emotions).map(([emotion, value]) => (
                                                    <div key={emotion}>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span className="text-stone-600 capitalize">{emotion}</span>
                                                            <span className="font-mono text-stone-900">{(value * 100).toFixed(0)}%</span>
                                                        </div>
                                                        <Progress value={value * 100} className="h-1.5" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Key Phrases */}
                                    {result.key_phrases?.length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium text-stone-700 mb-2">Key Phrases</p>
                                            <div className="flex flex-wrap gap-2">
                                                {result.key_phrases.map((phrase, i) => (
                                                    <Badge key={i} variant="outline" className="text-xs">
                                                        {phrase}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Transcript with Speaker Labels */}
                                    {result.transcript && (
                                        <div>
                                            <p className="text-sm font-medium text-stone-700 mb-2">
                                                Transcript {result.num_speakers > 1 ? '(with Speaker Labels)' : ''}
                                            </p>
                                            <div className="p-3 bg-stone-50 rounded-xl text-sm text-stone-600 max-h-60 overflow-y-auto whitespace-pre-wrap font-mono">
                                                {result.transcript}
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-64 text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mb-4">
                                        <Mic className="w-8 h-8 text-stone-400" />
                                    </div>
                                    <p className="text-stone-500">
                                        Upload and analyze an audio file to see results here.
                                    </p>
                                    <p className="text-xs text-stone-400 mt-2">
                                        Supports meetings & interviews with multiple speakers
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <Card className="bento-card">
                        <CardContent className="p-4 flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                <Mic className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                                <h4 className="font-medium text-stone-900">Multi-Speaker Detection</h4>
                                <p className="text-sm text-stone-500 mt-1">
                                    Automatically identifies and labels different speakers in meetings & interviews.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card">
                        <CardContent className="p-4 flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <h4 className="font-medium text-stone-900">AI Analysis</h4>
                                <p className="text-sm text-stone-500 mt-1">
                                    GPT-5.2 analyzes sentiment, emotions, and stress markers per speaker.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card">
                        <CardContent className="p-4 flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                                <AlertCircle className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <h4 className="font-medium text-stone-900">Privacy First</h4>
                                <p className="text-sm text-stone-500 mt-1">
                                    Audio is processed securely and not stored after analysis.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </motion.div>
        </div>
    );
}
